import React from 'react';
import { contrastRatio } from '@rocco/utils/colour-helpers';
import type { TagVariantColors } from '@modules/product-tagging/type';
import VariantDot from '@modules/tile/component/product-tile/VariantDot';
import { MAX_VARIANT_DOTS_ON_TILE } from '@modules/product/config/product-tile-config';

/**
 * Contrast ratio below which a swatch colour is too close to white
 * and needs a visible ring border so the dot is distinguishable.
 */
const RING_CONTRAST_THRESHOLD = contrastRatio('#efefef', '#ffffff');

interface BuildVariantDotsOptions {
    /** Variant colours coming from the product data. */
    variants: TagVariantColors[] | undefined;
    /** Server-supplied count of additional variants beyond the first page. */
    additionalVariantCount?: number;
    /** Whether the viewport is mobile — affects dot size and image key. */
    mobile: boolean;
    /** Diameter of each dot in px. Defaults to 13 (desktop) / 9 (mobile). */
    dotSize?: number;
}

interface BuildVariantDotsResult {
    /** Array of `{ key, children }` objects ready for Aria ProductTile/ProductInfo `variants` prop. */
    variantDots: { key: string; children: React.ReactNode }[] | undefined;
    /** Overflow count to pass as `additionalVariantCount`, or undefined when none. */
    adjustedAdditionalCount: number | undefined;
}

/**
 * Builds the variant colour dot elements for a product tile or product info component.
 *
 * Handles filtering out variants without a displayable dot or colour code,
 * capping at {@link MAX_VARIANT_DOTS_ON_TILE}, computing the "+N" overflow count,
 * and conditionally adding a ring border for swatches that are too close to white.
 */
export function buildVariantDots({
    variants,
    additionalVariantCount,
    mobile,
    dotSize,
}: BuildVariantDotsOptions): BuildVariantDotsResult {
    const imageKey = mobile ? 'mobile' : 'desktop';
    const size = dotSize ?? (mobile ? 9 : 13);

    const displayable = variants?.filter(
        ({ code, dot }) => dot?.[imageKey] || code,
    );

    if (!displayable || displayable.length <= 1) {
        return { variantDots: undefined, adjustedAdditionalCount: undefined };
    }

    const totalCount =
        additionalVariantCount != null
            ? additionalVariantCount + MAX_VARIANT_DOTS_ON_TILE
            : (variants?.length ?? 0);

    const dotsShown = Math.min(displayable.length, MAX_VARIANT_DOTS_ON_TILE);

    const variantDots = displayable
        .slice(0, MAX_VARIANT_DOTS_ON_TILE)
        .map(({ id, label, code, dot }) => {
            const colours = code?.split(',').filter(Boolean) ?? [];
            const isLowContrast = colours.some(
                (c) =>
                    contrastRatio(c.trim(), '#ffffff') <
                    RING_CONTRAST_THRESHOLD,
            );
            return {
                key: `variant-dot-${id}`,
                children: (
                    <VariantDot
                        size={size}
                        colorCode={code ?? ''}
                        description={label}
                        image={dot?.[imageKey]}
                        variables={
                            isLowContrast ? {} : { ringColor: 'transparent' }
                        }
                    />
                ),
            };
        });

    const adjustedAdditionalCount =
        Math.max(totalCount - dotsShown, 0) || undefined;

    return { variantDots, adjustedAdditionalCount };
}
